import * as React from 'react';
import { Trash2, Upload } from 'lucide-react';
import { useT } from '../lib/i18n.js';
import { saveMyPortrait } from '../lib/api.js';
import { cropToSquareDataUrl } from '../lib/resize-image.js';
import { useSession } from '../stores/session.js';

/**
 * Krok NAP3 bod 2 (A27) — Nastaveni -> Muj ucet -> karta „Portrét".
 *
 * Co chybelo: klientsky report (krok PROP7 bod 4) i profil na webu kancelare
 * portret UMI ZOBRAZIT, ale nahrat ho nesel nikde — `user.image` plnil jen
 * Better Auth pri prihlaseni treti stranou, takze makler, ktery se hlasi
 * e-mailem a heslem, mel v kontaktu reportu navzdy jen znacku kancelare.
 *
 * ODKUD SE BERE: soubor z pocitace nebo fotoaparatu telefonu, orezany na
 * ctverec v prohlizeci (`cropToSquareDataUrl`, tataz matematika jako zmenseni
 * fotek nemovitosti). KAM TECE: `PATCH /api/me/portrait` -> `user.image` ->
 * `getUserPortrait` -> kontaktni blok klientskeho reportu a profil na webu.
 *
 * Strop 150 kB je tentyz jako u loga kancelare (krok FAK4): drzi retezec pod
 * limitem `safeLogoUrl()` v `@reos/reports` — vetsi obrazek by se do dokumentu
 * tise nevlozil. Orez na 512 px se pod strop vejde i z fotky z telefonu.
 */
const MAX_PORTRAIT_BYTES = 150_000;

export function PortraitSettingsCard(): React.JSX.Element {
  const t = useT();
  const user = useSession((s) => s.user);
  const setUser = useSession((s) => s.setUserPortrait);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);
  const fileInput = React.useRef<HTMLInputElement>(null);
  const portrait = user?.portrait ?? null;

  async function pick(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file === undefined) return;
    setError(null);
    setSaved(false);
    setBusy(true);
    try {
      const dataUrl = await cropToSquareDataUrl(file);
      if (dataUrl.length > MAX_PORTRAIT_BYTES) {
        setError(t('webAdmin.settings.portraitTooLarge'));
        return;
      }
      setUser(await saveMyPortrait(dataUrl));
      setSaved(true);
    } catch {
      setError(t('webAdmin.settings.portraitSaveError'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    setError(null);
    setSaved(false);
    setBusy(true);
    try {
      setUser(await saveMyPortrait(null));
    } catch {
      setError(t('webAdmin.settings.portraitSaveError'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="rh-card"
      style={{ maxWidth: 640, padding: 16, marginBottom: 16 }}
      data-testid="nastaveni-portrait-card"
    >
      <h2 className="mb-1 text-base font-semibold">{t('webAdmin.settings.portraitTitle')}</h2>
      <p className="mb-3 text-sm text-gray-600">{t('webAdmin.settings.portraitPerex')}</p>

      {error !== null ? (
        <p role="alert" className="mb-2 text-sm text-red-600" data-testid="portrait-error">
          {error}
        </p>
      ) : null}
      {saved ? (
        <p className="mb-2 text-sm text-green-700" data-testid="portrait-saved">
          {t('webAdmin.settings.portraitSaved')}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        {portrait === null ? (
          <p className="m-0 text-sm text-gray-600" data-testid="portrait-empty">
            {t('webAdmin.settings.portraitEmpty')}
          </p>
        ) : (
          <img
            src={portrait}
            alt={t('webAdmin.settings.portraitAlt')}
            width={96}
            height={96}
            className="rounded-lg object-cover"
            data-testid="portrait-preview"
          />
        )}
        <button
          type="button"
          className="rh-btn"
          onClick={() => fileInput.current?.click()}
          disabled={busy}
          data-testid="portrait-upload"
        >
          <Upload size={16} aria-hidden="true" /> {t('webAdmin.settings.portraitUpload')}
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/avif,image/heic"
          className="hidden"
          aria-label={t('webAdmin.settings.portraitUpload')}
          data-testid="portrait-file-input"
          onChange={(event) => void pick(event)}
        />
        {portrait === null ? null : (
          <button
            type="button"
            className="rh-btn"
            onClick={() => void remove()}
            disabled={busy}
            data-testid="portrait-delete"
          >
            <Trash2 size={16} aria-hidden="true" /> {t('webAdmin.settings.portraitDelete')}
          </button>
        )}
      </div>
      <p className="mt-2 text-sm text-gray-600">{t('webAdmin.settings.portraitHint')}</p>
    </div>
  );
}
